import styled from '@emotion/styled';
import { useContext } from 'react';
import { supportUserContext } from 'application/contexts/useSupportUser';
import { supporterChatContext } from 'application/contexts/useSupporterChatRoom';
import { UserIcon } from 'ui/components/elements/UserIcon/UserIcon';
import { Message } from 'ui/components/agent/patterns/ChatRoom/Message';
import { MessageInput } from 'ui/components/features/MessageInput/MessageInput';
import { mqUser } from 'utils/responsive';
import { IChat } from 'domain/entities/Chat/Chat';

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 0 10px;
    position: relative;
    height: 100%;
    background: var(--white);

    ${mqUser('SP')} {
        padding: 0;
    }
`;

const CompanyNameContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
    font-weight: var(--font-bold);
    font-size: var(--font-x-large);
    margin-top: 8px;

    ${mqUser('SP')} {
        flex-direction: column;
        align-items: flex-start;
        font-size: 0.9rem;
        gap: 0px;
    }
`;

const BackButton = styled.button`
    display: none;
    align-items: center;
    background: none;
    border: none;
    padding: 0 10px;
    cursor: pointer;
    color: var(--main-color-blue);
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
    margin-right: auto;
    margin-top: -25px;

    ${mqUser('SP')} {
        display: flex;
    }
`;

const MessageContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    overflow-y: auto;
    flex: 1;
    padding: 10px 0;
`;

const MessageInputContainer = styled.div`
    padding: 10px 0;
    border-top: 1px solid #e0e0e0;
`;

interface SupporterChatRoomProps {
    onBack?: () => void;
}

export const SupporterChatRoom: React.FC<SupporterChatRoomProps> = ({ onBack }) => {
    const { loading, selectedChatRoom, message, setMessage, sendMessage, chatMessages } =
        useContext(supporterChatContext);
    const { user } = useContext(supportUserContext);

    if (!selectedChatRoom || !user) {
        return null;
    }

    const company = selectedChatRoom.company || selectedChatRoom.buyer;

    if (!company) {
        return null;
    }

    const onClickSendMessage = () => {
        if (message.trim()) {
            sendMessage();
        }
    };

    const getSenderUser = (chat: IChat) => {
        if (chat.sender_support_user?.id) {
            return chat.sender_support_user;
        }
        if (chat.sender_company_user?.id) {
            return chat.sender_company_user;
        }
        if (chat.sender_buyer_user?.id) {
            return chat.sender_buyer_user;
        }
        return null;
    };

    return (
        <Wrapper>
            <BackButton onClick={onBack}>← 戻る</BackButton>
            <CompanyNameContainer>
                <UserIcon size={40} src={company.icon_image} />
                <span>{company.name || '企業名未設定'}</span>
            </CompanyNameContainer>
            <MessageContainer>
                {chatMessages.length === 0 && !loading ? (
                    <div style={{ textAlign: 'center', color: '#5f5f5f', marginTop: '20px' }}>
                        まだメッセージがありません。
                    </div>
                ) : (
                    chatMessages.map((chat) => {
                        const sender = getSenderUser(chat);
                        return (
                            <Message
                                key={chat.id}
                                icon_image={sender?.icon_image || ''}
                                name={
                                    sender && sender.last_name
                                        ? `${sender.last_name} ${sender.first_name}`
                                        : '名前未設定'
                                }
                                isSelf={chat?.sender_support_user?.id === user.id}
                                date={chat.send_at}
                                message={chat.message}
                            />
                        );
                    })
                )}
            </MessageContainer>
            <MessageInputContainer>
                <MessageInput
                    archived={false}
                    message={message}
                    setMessage={setMessage}
                    sendMessage={onClickSendMessage}
                    requesting={loading}
                    companyType="supporter"
                />
            </MessageInputContainer>
        </Wrapper>
    );
};
